import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  MenuItem,
  Skeleton,
  IconButton,
  Autocomplete,
  TextField as MuiTextField,
  useTheme,
} from "@mui/material";
import {
  get_profile,
  update_profile,
  get_country,
  get_state,
  get_city,
} from "../../api/ControllApi";
import { useSettings } from "../../context/Settings";
import useCachedApi from "../../hooks/useCachedApi";
import { toast } from "react-toastify";
import { useUser } from "../../context/LoginDetail";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

const Account = () => {
  const [profileData, setProfileData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    dob: "",
    phone: "",
    pin_code: "",
    gender: "",
    address: "",
    image: "",
    country_id: "",
    state_id: "",
    city_id: "",
    reference_id: "",
    referral_code: "",
  });

  const [errors, setErrors] = useState({});
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [updatingProfile, setUpdatingProfile] = useState(false);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");

  const [profileImage, setProfileImage] = useState("");
  const { user, loadUser } = useUser();
  const { requestApi } = useCachedApi();
  const theme = useTheme();
  const { settings } = useSettings();

  // reference_id === 1 or missing → editable (referred by admin or no one)
  // reference_id !== 1 → non-editable, show refer.code
  const isEditable = Number(profileData.reference_id) === 1 || !profileData.reference_id;

  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        dob: user.dob || "",
        phone: user.phone || "",
        pin_code: user.pin || "",
        gender: user.gender || "",
        address: user.address || "",
        image: "",
        country_id: user.country_id || "",
        state_id: user.state_id || "",
        city_id: user.city_id || "",
        reference_id: user.reference_id || "",
        referral_code: (Number(user.reference_id) === 1 || !user.reference_id) ? "" : (user.refer?.code || ""),
      });
      setProfileImage(user.avatar || "/assets/images/profile2.png");
      setLoadingProfile(false);
    }
  }, [user]);

  useEffect(() => {
    requestApi(`countries-${countrySearch}`, () =>
      get_country({ search: countrySearch })
    )
      .then((res) => setCountries(res?.data || []))
      .catch(() => {});
  }, [countrySearch, requestApi]);

  useEffect(() => {
    if (profileData.country_id) {
      requestApi(
        `states-${profileData.country_id}-${stateSearch}`,
        () => get_state({ country_id: profileData.country_id, search: stateSearch })
      )
        .then((res) => setStates(res?.data || []))
        .catch(() => {});
    } else {
      setStates([]);
    }
  }, [profileData.country_id, stateSearch, requestApi]);

  useEffect(() => {
    if (profileData.state_id) {
      requestApi(
        `cities-${profileData.state_id}-${citySearch}`,
        () => get_city({ state_id: profileData.state_id, search: citySearch })
      )
        .then((res) => setCities(res?.data || []))
        .catch(() => {});
    } else {
      setCities([]);
    }
  }, [profileData.state_id, citySearch, requestApi]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => {
      const newErrors = { ...prev };

      if (name === "email") {
        if (!value) {
          newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors.email = "Enter a valid email address";
        } else {
          delete newErrors.email;
        }
      } else if (["first_name", "last_name", "address"].includes(name)) {
        if (!value.trim()) {
          newErrors[name] = `${name.replace("_", " ")} is required`;
        } else {
          delete newErrors[name];
        }
      } else if (name === "pin_code") {
        if (!value) {
          newErrors.pin_code = "Pin code is required";
        } else if (value.length !== 6) {
          newErrors.pin_code = "Pin code must be 6 digits";
        } else {
          delete newErrors.pin_code;
        }
      } else if (name === "gender") {
        if (!value) {
          newErrors.gender = "Please select gender";
        } else {
          delete newErrors.gender;
        }
      }

      return newErrors;
    });
  };

  const validateProfile = () => {
    const newErrors = {};

    if (!profileData.first_name.trim())
      newErrors.first_name = "First name is required";

    if (!profileData.last_name.trim())
      newErrors.last_name = "Last name is required";

    if (!profileData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      newErrors.email = "Enter a valid email address";
    }

    if (!profileData.dob) newErrors.dob = "Date of birth is required";
    if (!profileData.phone) newErrors.phone = "Phone number is required";

    if (!profileData.pin_code) {
      newErrors.pin_code = "Pin code is required";
    } else if (profileData.pin_code.length !== 6) {
      newErrors.pin_code = "Pin code must be 6 digits";
    }

    if (!profileData.gender) newErrors.gender = "Please select gender";
    if (!profileData.country_id) newErrors.country_id = "Country is required";
    if (!profileData.state_id) newErrors.state_id = "State is required";
    if (!profileData.city_id) newErrors.city_id = "City is required";
    if (!profileData.address.trim()) newErrors.address = "Address is required";

    setErrors(newErrors);
    return newErrors;
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/jfif"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid image (jpg, jpeg, png, jfif)");
      return;
    }

    const url = URL.createObjectURL(file);
    setProfileImage(url);
    setProfileData((prev) => ({ ...prev, image: file }));
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const errors = validateProfile();
    if (Object.keys(errors).length > 0) return;

    setUpdatingProfile(true);
    try {
      const formData = new FormData();
      for (let key in profileData) {
        if (key === "image" && profileData[key] instanceof File) {
          formData.append(key, profileData[key]);
        } else if (key === "reference_id") {
          // If referred by admin (1), send the entered code as reference_id
          const refValue = (Number(profileData.reference_id) === 1 && profileData.referral_code) 
            ? profileData.referral_code 
            : profileData.reference_id;
          formData.append(key, refValue || "");
        } else if (key !== "image" && key !== "referral_code") {
          formData.append(key, profileData[key] || "");
        }
      }

      await update_profile(formData);
      toast.success("Profile updated successfully!");
      loadUser();
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.errors) {
        Object.values(errorData.errors).forEach((errorGroup) => {
          if (Array.isArray(errorGroup)) {
            errorGroup.forEach((msg) => toast.error(msg));
          } else {
            toast.error(errorGroup);
          }
        });
      } else {
        toast.error(errorData?.message || err.message || "Failed to update profile");
      }
    } finally {
      setUpdatingProfile(false);
    }
  };

  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: 1100,
        p: { xs: 3, sm: 5, md: 6 },
        borderRadius: 4,
        boxShadow: 6,
        backgroundImage: "url(/assets/images/plotbg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {!loadingProfile ? (
        <>
          {/* Profile Image */}
          <center>
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                cursor: "pointer",
                mb: 4,
              }}
              onClick={() => document.getElementById("profileInput").click()}
            >
              <input
                type="file"
                id="profileInput"
                accept=".png,.jpeg,.jpg"
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
              <img
                src={profileImage || "/assets/images/placeholder_img.png"}
                alt="profile"
                style={{
                  width: 110,
                  height: 110,
                  borderRadius: "50%",
                  border: "4px solid white",
                  boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
                  objectFit: "cover",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  backgroundColor: "#92AF5D",
                  borderRadius: "50%",
                  width: 30,
                  height: 30,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid white",
                }}
              >
                <EditIcon style={{ width: 16, height: 16, color: "#fff" }} />
              </Box>
            </Box>

            <Typography sx={{ mb: 1, fontSize: 20, fontWeight: 600 }}>
              {profileData?.name || "Profile"}
            </Typography>

            {(Number(user?.is_vendor) === 1 || Number(user?.is_agent) === 1) &&
              user?.code && (
                <Box
                  sx={{
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{ fontSize: 14, color: "text.secondary", fontWeight: 500 }}
                  >
                    Referral ID:
                  </Typography>
                  <Typography
                    sx={{ fontSize: 14, color: "primary.main", fontWeight: 700 }}
                  >
                    {user.code}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => {
                      navigator.clipboard.writeText(user.code);
                      toast.success("Referral ID copied!");
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
          </center>

          {/* Form */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            <TextField
              label="First Name*"
              size="small"
              fullWidth
              name="first_name"
              value={profileData.first_name || ""}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                handleChange(e);
              }}
              error={!!errors.first_name}
              helperText={errors.first_name}
            />

            <TextField
              label="Last Name*"
              size="small"
              fullWidth
              name="last_name"
              value={profileData.last_name}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                handleChange(e);
              }}
              error={!!errors.last_name}
              helperText={errors.last_name}
            />

            <TextField
              label="Email Id*"
              size="small"
              fullWidth
              name="email"
              value={profileData.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />

            <Autocomplete
              options={countries}
              getOptionLabel={(option) => option.country_name || ""}
              openOnFocus
              autoHighlight
              filterOptions={(x) => x}
              forcePopupIcon={true}
              value={countries.find((c) => c.id === profileData.country_id) || null}
              onChange={(event, newValue) => {
                setProfileData((prev) => ({
                  ...prev,
                  country_id: newValue ? newValue.id : "",
                  state_id: "",
                  city_id: "",
                }));
                setErrors((prev) => {
                  const newErrs = { ...prev };
                  delete newErrs.country_id;
                  delete newErrs.state_id;
                  delete newErrs.city_id;
                  return newErrs;
                });
              }}
              onInputChange={(event, newInputValue) => setCountrySearch(newInputValue)}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  label="Country*"
                  placeholder="Select Country"
                  size="small"
                  fullWidth
                  error={!!errors.country_id}
                  helperText={errors.country_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", height: "38px" } }}
                />
              )}
            />

            <Autocomplete
              options={states}
              getOptionLabel={(option) => option.state_name || ""}
              openOnFocus
              autoHighlight
              filterOptions={(x) => x}
              forcePopupIcon={true}
              value={states.find((s) => s.id === profileData.state_id) || null}
              onChange={(event, newValue) => {
                setProfileData((prev) => ({
                  ...prev,
                  state_id: newValue ? newValue.id : "",
                  city_id: "",
                }));
                setErrors((prev) => {
                  const newErrs = { ...prev };
                  delete newErrs.state_id;
                  delete newErrs.city_id;
                  return newErrs;
                });
              }}
              onInputChange={(event, newInputValue) => setStateSearch(newInputValue)}
              disabled={!profileData.country_id}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  label="State*"
                  placeholder="Select State"
                  size="small"
                  fullWidth
                  error={!!errors.state_id}
                  helperText={errors.state_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", height: "38px" } }}
                />
              )}
            />

            <Autocomplete
              options={cities}
              getOptionLabel={(option) => option.city_name || ""}
              openOnFocus
              autoHighlight
              filterOptions={(x) => x}
              forcePopupIcon={true}
              value={cities.find((c) => c.id === profileData.city_id) || null}
              onChange={(event, newValue) => {
                setProfileData((prev) => ({
                  ...prev,
                  city_id: newValue ? newValue.id : "",
                }));
                setErrors((prev) => {
                  const newErrs = { ...prev };
                  delete newErrs.city_id;
                  return newErrs;
                });
              }}
              onInputChange={(event, newInputValue) => setCitySearch(newInputValue)}
              disabled={!profileData.state_id}
              renderInput={(params) => (
                <MuiTextField
                  {...params}
                  label="City*"
                  placeholder="Select City"
                  size="small"
                  fullWidth
                  error={!!errors.city_id}
                  helperText={errors.city_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", height: "38px" } }}
                />
              )}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date Of Birth*"
                value={profileData.dob ? dayjs(profileData.dob) : null}
                onChange={(newValue) => {
                  setProfileData({
                    ...profileData,
                    dob: newValue ? newValue.format("YYYY-MM-DD") : "",
                  });
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: !!errors.dob,
                    helperText: errors.dob,
                  },
                }}
              />
            </LocalizationProvider>

            <TextField
              label="Phone Number*"
              size="small"
              fullWidth
              name="phone"
              disabled
              value={profileData.phone}
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />

            <TextField
              label="Pin Code*"
              size="small"
              fullWidth
              name="pin_code"
              value={profileData.pin_code}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
                handleChange(e);
              }}
              error={!!errors.pin_code}
              helperText={errors.pin_code}
            />

            <TextField
              select
              label="Gender*"
              size="small"
              fullWidth
              name="gender"
              value={profileData.gender}
              onChange={handleChange}
              error={!!errors.gender}
              helperText={errors.gender}
            >
              <MenuItem value="1">Male</MenuItem>
              <MenuItem value="2">Female</MenuItem>
            </TextField>

            <TextField
              label="Referral Code"
              size="small"
              fullWidth
              name="referral_code"
              value={profileData.referral_code || ""}
              onChange={handleChange}
              placeholder="Enter Referral Code"
              disabled={!isEditable}
            />

            <TextField
              label="Address*"
              size="small"
              fullWidth
              name="address"
              value={profileData.address}
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
            />
          </Box>

          {/* Update Button */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <Button
              variant="contained"
              type="button"
              sx={{
                backgroundColor: "primary.main",
                py: 1.5,
                px: 6,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: 16,
              }}
              onClick={handleUpdateProfile}
              disabled={updatingProfile}
            >
              {updatingProfile ? "Updating..." : "Update Profile"}
            </Button>
          </Box>
        </>
      ) : (
        <Box>
          <Skeleton
            height={120}
            width={120}
            sx={{ mx: "auto", mb: 3 }}
            variant="circular"
          />
          <Skeleton height={30} width={180} sx={{ mx: "auto", mb: 4 }} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={60} />
          </Box>
        </Box>
      )}
    </Card>
  );
};

export default Account;